import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Loader2, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useGetPreSignedUrlForUpload } from '@/lib/api/hooks/use-storage';
import API_CONFIG from '@/config/api';
import { validateImage, ACCEPTED_IMAGE_TYPES } from '../../utils/file-validation';

/**
 * HotWheelImageUploader — single-image uploader.
 *
 * Supports:
 *   - Click anywhere on the dropzone to open the file picker.
 *   - Drag and drop.
 *
 * Flow:
 *   1. User picks / drops a file.
 *   2. Client-side validation (size + type).
 *   3. GET /data/v4/Files/GetPreSignedUrlForUpload → { uploadUrl, fileId }.
 *   4. PUT uploadUrl with the binary (+ x-ms-blob-type: BlockBlob).
 *   5. Fire onChange(fileId, previewUrl) so the parent can store the fileId.
 *
 * Props:
 *   - value: current fileId (or null if none).
 *   - onChange: (fileId, previewUrl) => void.
 */

interface HotWheelImageUploaderProps {
  value: string | null;
  onChange: (fileId: string | null, previewUrl: string | null) => void;
  className?: string;
  /** Existing preview URL (e.g. resolved from a stored fileId) shown when no new file is selected. */
  existingPreviewUrl?: string | null;
}

export function HotWheelImageUploader({
  value,
  onChange,
  className,
  existingPreviewUrl,
}: HotWheelImageUploaderProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutateAsync: getPreSignedUrlAsync } = useGetPreSignedUrlForUpload();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const showPreview = previewUrl || existingPreviewUrl || null;

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    try {
      const data = await getPreSignedUrlAsync({
        name: file.name,
        projectKey: API_CONFIG.blocksKey,
        itemId: '',
        metaData: '',
        accessModifier: 'Public',
        configurationName: 'Default',
        parentDirectoryId: '',
        tags: '',
      });
      if (!data.isSuccess || !data.uploadUrl || !data.fileId) {
        throw new Error(
          data.errors
            ? Object.values(data.errors).join('; ')
            : 'Failed to get upload URL'
        );
      }
      const putResponse = await fetch(data.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
          'x-ms-blob-type': 'BlockBlob',
        },
      });
      if (!putResponse.ok) {
        throw new Error(`Upload failed: ${putResponse.status}`);
      }
      const newPreview = URL.createObjectURL(file);
      setPreviewUrl(newPreview);
      onChange(data.fileId, newPreview);
    } catch (err) {
      toast({
        title: t('UPLOAD_FAILED'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const result = validateImage(file);
    if (!result.valid) {
      toast({ title: t('INVALID_FILE'), description: result.error, variant: 'destructive' });
      return;
    }
    uploadFile(file);
  };

  const handleClick = () => {
    if (isUploading) return;
    inputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer?.files ?? null);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewUrl(null);
    onChange(null, null);
  };

  if (showPreview) {
    return (
      <div className={cn('relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-blue-50', className)}>
        <img src={showPreview} alt="Car preview" className="w-full h-full object-cover" />
        {isUploading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-white animate-spin" />
            <span className="ml-2 text-white text-sm">{t('UPLOADING')}</span>
          </div>
        )}
        {!isUploading && (
          <Button
            type="button"
            size="icon"
            variant="destructive"
            onClick={handleRemove}
            className="absolute top-3 right-3 rounded-full"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'relative w-full aspect-[4/3] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500',
        isDragging
          ? 'border-blue-500 bg-blue-50'
          : 'border-blue-300 bg-blue-50/40 hover:border-blue-500 hover:bg-blue-50',
        isUploading && 'pointer-events-none opacity-60',
        className
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        className="sr-only"
        onChange={(e) => handleFiles(e.target.files)}
        disabled={isUploading}
      />
      <div className="flex flex-col items-center gap-3 text-center p-6">
        <div className="rounded-full bg-blue-100 p-4">
          {isUploading ? (
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          ) : (
            <Upload className="h-8 w-8 text-blue-600" />
          )}
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-slate-800">
            {isUploading ? t('UPLOADING') : t('DRAG_DROP_IMAGE')}
          </p>
          <p className="text-xs text-slate-500">{t('OR_CLICK_TO_BROWSE')}</p>
        </div>
        <p className="text-xs text-slate-400">{t('IMAGE_REQUIREMENTS')}</p>
      </div>
    </div>
  );
}